import { useState } from 'react';
import styled from 'styled-components';
import isNil from 'lodash/isNil';
import {
  autoUpdate,
  FloatingPortal,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import ExpressionMeasureSettingsForm from './ExpressionMeasureSettingsForm';
import { getLineTypeLabel } from '../../../../RollingControlDisplay/ExpressionSettingLineTypeView';
import { assertCondition, assertNotNil } from '../../../../../utils/types';
import { QueryType } from '../../../../../utils/apollo/graphqlSchema';
import { CHART_TYPES } from '../../../constants/chartTypes';
import { useFloatingLayerContext } from '../../../../FloatingManager';
import BorderedDropdownLabel from '../../../../Dropdown/BorderedDropdownLabel';
import { floatingComponentStyles } from '../../../../../styles/shareableStyles';
import { useChartContext } from '../../../ChartContext';
interface IProps {
  analysisId: string;
}

const MenuWrapper = styled.div`
  min-width: 160px;
  ${floatingComponentStyles};
`;

export default ({ analysisId }: IProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const { floatingStyles, refs, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    placement: 'bottom-start',
  });

  const {
    metadata: { query, startDate, endDate, comparisonInfo },
    settings: { chartSettings },
    chartData,
  } = useChartContext();
  const { expressionSettings } = chartSettings;
  assertCondition(query?.__typename === 'ExpressionQuery');
  assertNotNil(expressionSettings);
  const { chartType } = chartSettings;
  assertCondition(chartData?.queryType === QueryType.Expression);
  const dataPointsAmount = chartData?.data?.length ?? 0;
  const { measure } = query;

  // Check if there is a simple measure with rolling control
  const isRollingControlActive = !isNil(measure?.rollingControl);

  const isDisabled = startDate === endDate || isRollingControlActive;

  const isMeasureToggleDisabled = chartType === CHART_TYPES.METRIC_CHART;
  const showCompareOption = !isNil(comparisonInfo) && !comparisonInfo.isEmpty;

  const click = useClick(context, { enabled: !isDisabled });
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss,
    role,
  ]);

  const renderLabel = () => {
    const { showMeasure, showCompare, lineType } = expressionSettings;

    const result: string[] = [];

    if (showMeasure) {
      result.push('Measure');
    }

    if (showCompare) {
      result.push('C. Measure');
    }

    if (lineType !== '') {
      result.push(getLineTypeLabel(lineType));
    }

    if (result.length > 0) {
      return result.join(', ');
    }

    // Return placeholder if nothing selected
    return 'None';
  };

  const zIndex = useFloatingLayerContext();

  return (
    <>
      <BorderedDropdownLabel
        ref={refs.setReference}
        {...getReferenceProps()}
        isDisabled={isDisabled}
      >
        {renderLabel()}
      </BorderedDropdownLabel>
      {isOpen && (
        <FloatingPortal>
          <MenuWrapper
            ref={refs.setFloating}
            style={{ ...floatingStyles, zIndex }}
            {...getFloatingProps()}
          >
            <ExpressionMeasureSettingsForm
              analysisId={analysisId}
              expressionSettings={expressionSettings}
              dataPointsAmount={dataPointsAmount}
              isMeasureToggleDisabled={isMeasureToggleDisabled}
              showCompareOption={showCompareOption}
            />
          </MenuWrapper>
        </FloatingPortal>
      )}
    </>
  );
};
